import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { BrandingProvider } from './contexts/BrandingContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import BrandingWrapper from './components/common/BrandingWrapper';
// Login pages stay eager -- every visitor hits one of these first,
// regardless of role, so lazy-loading them would only add a loading
// flicker to the very first paint with no bundle-size benefit (everyone
// needs this code anyway).
import Login from './pages/Login';
import SuperAdminLogin from './pages/SuperAdminLogin';

// Everything below is role-specific and lazy-loaded so a given visitor only
// ever downloads the code for the portal they actually use -- a student
// never pulls in Super Admin's question bank, coaching management, etc.,
// and vice versa. Each import() becomes its own chunk at build time.

// Super Admin pages
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdmin/Dashboard'));
const CoachingManagement = lazy(() => import('./pages/SuperAdmin/CoachingManagement'));
const UploadTest = lazy(() => import('./pages/SuperAdmin/UploadTest'));
const SuperAdminMyTests = lazy(() => import('./pages/SuperAdmin/MyTests'));
const EditTest = lazy(() => import('./pages/SuperAdmin/EditTest'));
const CreateTest = lazy(() => import('./pages/SuperAdmin/CreateTest'));
const QuestionBank = lazy(() => import('./pages/SuperAdmin/QuestionBank'));
const SuperAdminAnalytics = lazy(() => import('./pages/SuperAdmin/Analytics'));
const AttemptsReport = lazy(() => import('./pages/SuperAdmin/AttemptsReport'));
const AttemptDetail = lazy(() => import('./pages/SuperAdmin/AttemptDetail'));

// Coaching Admin pages
const CoachingAdminDashboard = lazy(() => import('./pages/CoachingAdmin/Dashboard'));
const BatchManager = lazy(() => import('./pages/CoachingAdmin/BatchManager'));
const StudentManager = lazy(() => import('./pages/CoachingAdmin/StudentManager'));
const TeacherManager = lazy(() => import('./pages/CoachingAdmin/TeacherManager'));
const TestAssignment = lazy(() => import('./pages/CoachingAdmin/TestAssignment'));
const Schedule = lazy(() => import('./pages/CoachingAdmin/Schedule'));
const CoachingResults = lazy(() => import('./pages/CoachingAdmin/Results'));

// Teacher pages
const TeacherDashboard = lazy(() => import('./pages/Teacher/Dashboard'));
const TestGenerator = lazy(() => import('./pages/Teacher/TestGenerator'));
const TeacherTests = lazy(() => import('./pages/Teacher/MyTests'));

// Student pages
const StudentDashboard = lazy(() => import('./pages/Student/Dashboard'));
const StudentTests = lazy(() => import('./pages/Student/MyTests'));
const TestEngine = lazy(() => import('./pages/Student/TestEngine'));
const ResultDashboard = lazy(() => import('./pages/Student/ResultDashboard'));
const AnswerReview = lazy(() => import('./pages/Student/AnswerReview'));
const StudentResults = lazy(() => import('./pages/Student/Results'));

function RouteLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />
    </div>
  );
}

function Unauthorized() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-800 mb-2">403</h1>
        <p className="text-gray-600">You don't have permission to access this page.</p>
        <a href="/login" className="text-purple-600 hover:underline mt-4 inline-block">Back to Login</a>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandingProvider>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              {/* Super Admin login — public, no auth needed */}
              <Route path="/super-admin" element={<SuperAdminLogin />} />
              <Route path="/unauthorized" element={<Unauthorized />} />

              {/* Super Admin protected — redirects to /super-admin (not /login) when unauthenticated */}
              <Route element={<ProtectedRoute roles={['super_admin']} redirectTo="/super-admin"><BrandingWrapper /></ProtectedRoute>}>
                <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
                <Route path="/super-admin/coachings" element={<CoachingManagement />} />
                <Route path="/super-admin/upload-test" element={<UploadTest />} />
                <Route path="/super-admin/my-tests" element={<SuperAdminMyTests />} />
                <Route path="/super-admin/edit-test/:testId" element={<EditTest />} />
                <Route path="/super-admin/create-test" element={<CreateTest />} />
                <Route path="/super-admin/questions" element={<QuestionBank />} />
                <Route path="/super-admin/analytics" element={<SuperAdminAnalytics />} />
                <Route path="/super-admin/attempts-report" element={<AttemptsReport />} />
                <Route path="/super-admin/attempts/:attemptId" element={<AttemptDetail />} />
              </Route>

              {/* Coaching Admin — role stored as 'teacher' in legacy accounts, 'coaching_admin' in new ones */}
              <Route element={<ProtectedRoute roles={['coaching_admin', 'teacher']}><BrandingWrapper /></ProtectedRoute>}>
                <Route path="/coaching-admin" element={<CoachingAdminDashboard />} />
                <Route path="/coaching-admin/batches" element={<BatchManager />} />
                <Route path="/coaching-admin/students" element={<StudentManager />} />
                <Route path="/coaching-admin/teachers" element={<TeacherManager />} />
                <Route path="/coaching-admin/tests" element={<TestAssignment />} />
                <Route path="/coaching-admin/schedule" element={<Schedule />} />
                <Route path="/coaching-admin/results" element={<CoachingResults />} />
                <Route path="/coaching-admin/generate" element={<TestGenerator />} />
              </Route>

              {/* Teacher */}
              <Route element={<ProtectedRoute roles={['teacher']}><BrandingWrapper /></ProtectedRoute>}>
                <Route path="/teacher" element={<TeacherDashboard />} />
                <Route path="/teacher/generate" element={<TestGenerator />} />
                <Route path="/teacher/tests" element={<TeacherTests />} />
              </Route>

              {/* Student */}
              <Route element={<ProtectedRoute roles={['student']}><BrandingWrapper /></ProtectedRoute>}>
                <Route path="/student" element={<StudentDashboard />} />
                <Route path="/student/tests" element={<StudentTests />} />
                <Route path="/student/test/:assignmentId" element={<TestEngine />} />
                <Route path="/student/result/:attemptId" element={<ResultDashboard />} />
                <Route path="/student/result/:attemptId/review" element={<AnswerReview />} />
                <Route path="/student/results" element={<StudentResults />} />
              </Route>

              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
        </BrandingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
